import React, { useState, useEffect, useMemo } from "react";
import {
    Box, TextField, Button, Typography, Divider, Card, Grid
} from "@mui/material";
import { db } from "../../firebase";
import { collection, getDocs, query, where, Timestamp, orderBy, onSnapshot } from "firebase/firestore";

function Collectionreport() {
    const today = new Date().toISOString().split("T")[0];
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);

    // Master Data States
    const [customers, setCustomers] = useState([]);
    const [payModes, setPayModes] = useState([]);

    // 1. Real-time Listeners for Master Data (Customers & Payment Modes)
    useEffect(() => {
        const unsubCustomers = onSnapshot(collection(db, "customers"), (snap) => {
            setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        const unsubPayModes = onSnapshot(collection(db, "payment_modes"), (snap) => {
            setPayModes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => {
            unsubCustomers();
            unsubPayModes();
        };
    }, []);

    // 2. Create Fast Lookup Maps (Automatically replaces IDs with Names)
    const customerMap = useMemo(() => {
        const map = new Map();
        customers.forEach(c => map.set(c.id, c.customerName));
        return map;
    }, [customers]);

    const payModeMap = useMemo(() => {
        const map = new Map();
        payModes.forEach(m => {
            // Map both the Document ID and the 'payMode' string for maximum compatibility
            map.set(m.id, m.payMode); 
            if (m.payMode) map.set(m.payMode, m.payMode);
        });
        return map;
    }, [payModes]);

    // 3. Fetch Transactional History
    const fetchAllData = async () => {
        if (!fromDate || !toDate) return alert("Please select a date range");
        setLoading(true);

        try {
            const start = Timestamp.fromDate(new Date(fromDate + " 00:00:00"));
            const end = Timestamp.fromDate(new Date(toDate + " 23:59:59"));

            const qPay = query(
                collection(db, "customer_history"), 
                where("date", ">=", start), 
                where("date", "<=", end), 
                orderBy("date", "desc")
            );

            const paySnap = await getDocs(qPay);
            setPayments(paySnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
            console.error("Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    // Auto-fetch on component mount
    useEffect(() => { fetchAllData(); }, []);

    // 4. Styles
    const cellStyle = { border: '1px solid #000', padding: '8px', fontSize: '0.85rem' };
    const headerStyle = { ...cellStyle, fontWeight: 'bold', backgroundColor: '#f2f2f2', textAlign: 'center' };

    return (
        <Box p={2} sx={{ bgcolor: "#fff", minHeight: '100vh' }}>
            {/* Filter Bar */}
            <Card sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', borderRadius: '8px' }} className="no-print">
                <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }} value={fromDate} onChange={e => setFromDate(e.target.value)} />
                <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }} value={toDate} onChange={e => setToDate(e.target.value)} />
                <Button variant="contained" onClick={fetchAllData} disabled={loading} sx={{ minWidth: '120px' }}>
                    {loading ? "Loading..." : "Generate"}
                </Button>
                <Button variant="outlined" onClick={() => window.print()}>Print PDF</Button>
            </Card>

            {/* Report Content */}
            <Box className="printable-area">
                <Box textAlign="center" mb={2}>
                    {/* <Typography variant="h5" fontWeight="bold">TEJAS STONE CRUSHER</Typography>
                    <Typography variant="body2">Varuda Road, Dist. Dharashiv, Maharashtra</Typography> */}
                    <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold', textDecoration: 'underline' }}>
                        COLLECTION REPORT
                    </Typography>
                    <Typography variant="body2">({fromDate} TO {toDate})</Typography>
                </Box>

                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ ...headerStyle, width: '50px' }}>Sr.</th>
                            <th style={headerStyle}>Date</th>
                            <th style={headerStyle}>Customer Name</th>
                            <th style={headerStyle}>Payment Mode</th>
                            <th style={headerStyle}>Paid Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {payments.length > 0 ? payments.map((row, index) => {
                            // Resolve Customer Name (Checks customerId or customer field)
                            const rawCust = row.customerId || row.customer;
                            const displayName = customerMap.get(rawCust) || row.customerName || rawCust || "N/A";

                            // Resolve Payment Mode
                            const rawMode = row.paymentMode || row.payMode;
                            const displayMode = payModeMap.get(rawMode) || rawMode || "N/A";

                            return (
                                <tr key={row.id}>
                                    <td style={{ ...cellStyle, textAlign: 'center' }}>{index + 1}</td>
                                    <td style={{ ...cellStyle, textAlign: 'center' }}>
                                        {row.date?.toDate().toLocaleDateString('en-GB')}
                                    </td>
                                    <td style={cellStyle}><strong>{displayName}</strong></td>
                                    <td style={{ ...cellStyle, textAlign: 'center' }}>{displayMode}</td>
                                    <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                                        ₹{Number(row.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </td>
                                </tr>
                            );
                        }) : (
                            <tr>
                                <td colSpan={5} style={{ ...cellStyle, textAlign: 'center', p: 3 }}>No data found.</td>
                            </tr>
                        )}
                        {/* Summary Row */}
                        <tr style={{ backgroundColor: '#f9f9f9' }}>
                            <td colSpan={4} style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '1rem' }}>
                                TOTAL COLLECTION
                            </td>
                            <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '1rem', color: '#1b5e20' }}>
                                ₹{payments.reduce((s, i) => s + (Number(i.amount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </Box>

            <style>{`
                @media print {
                    @page { size: A4; margin: 15mm; }
                    .no-print { display: none !important; }
                    body { background: white; margin: 0; padding: 0; }
                    .printable-area { width: 100%; border: none; }
                    table { page-break-inside: auto; }
                    tr { page-break-inside: avoid; page-break-after: auto; }
                }
            `}</style>
        </Box>
    );
}

export default Collectionreport;